/**
 * Product page: variant picker and gallery (templates/_pages/product.twig).
 *
 * config.variants: [{ id, sku, salePrice, price, onPromotion, hasStock, stock, purchasable, values: [valueId, …], imageId, url }]
 * config.attributes: [{ id, title, display, values: [{ id, title, color, image }] }]
 * config.defaultVariantId, config.imageIds: gallery image ids in order
 * config.messages: the stock messages per state, { title, text }, with {selection} and {count} to fill in
 *
 * `selected` holds the picked value per attribute: { attributeId: valueId }.
 */
window.productPage = function (config) {
    return {
        variants: config.variants,
        attributes: config.attributes,
        selected: {},
        activeImageId: config.imageIds[0] ?? null,

        init() {
            // A ?variant= link preselects that variant
            const requestedId = Number(new URLSearchParams(window.location.search).get('variant'));
            const start = this.variants.find(variant => variant.id === requestedId)
                ?? this.variants.find(variant => variant.id === config.defaultVariantId)
                ?? this.variants[0];
            if (start) {
                this.selected = this.selectionOf(start);
                this.showVariantImage(start);
            }

            this.$watch('selected', () => {
                const variant = this.variant;
                if (!variant) {
                    return;
                }
                this.showVariantImage(variant);
                if (this.attributes.length && variant.url) {
                    window.history.replaceState(null, '', variant.url);
                }
            });
        },

        // The variant with every selected value
        get variant() {
            if (!this.attributes.length) {
                return this.variants.find(variant => variant.id === config.defaultVariantId) ?? this.variants[0] ?? null;
            }
            return this.findVariant(this.selected) ?? null;
        },

        get canBuy() {
            return Boolean(this.variant && this.variant.purchasable && this.variant.hasStock);
        },

        findVariant(selection) {
            return this.variants.find(variant => this.attributes.every(attribute => variant.values.includes(selection[attribute.id])));
        },

        // A variant's value for each attribute
        selectionOf(variant) {
            return Object.fromEntries(this.attributes.map(attribute => [
                attribute.id,
                attribute.values.find(value => variant.values.includes(value.id))?.id ?? null,
            ]));
        },

        isSelected(attributeId, valueId) {
            return this.selected[attributeId] === valueId;
        },

        // Whether a variant exists with this value and the other selected values
        isAvailable(attributeId, valueId) {
            return this.variants.some(variant => variant.values.includes(valueId)
                && this.attributes.every(attribute => attribute.id === attributeId || variant.values.includes(this.selected[attribute.id])));
        },

        // Picking a value that doesn't combine with the others switches to the first variant that has it
        select(attributeId, valueId) {
            const selection = { ...this.selected, [attributeId]: valueId };
            if (this.findVariant(selection)) {
                this.selected = selection;
                return;
            }
            const fallback = this.variants.find(variant => variant.values.includes(valueId));
            this.selected = fallback ? this.selectionOf(fallback) : selection;
        },

        // Whether the variant with this value and the other selected values is out of stock
        isSoldOut(attributeId, valueId) {
            const variant = this.findVariant({ ...this.selected, [attributeId]: valueId });
            return Boolean(variant && !variant.hasStock);
        },

        selectedTitle(attributeId) {
            const attribute = this.attributes.find(attribute => attribute.id === attributeId);
            return attribute?.values.find(value => value.id === this.selected[attributeId])?.title ?? '';
        },

        // The selection in words, for the stock messages: "size M"
        get selectionLabel() {
            return this.attributes
                .map(attribute => `${attribute.title.toLowerCase()} ${this.selectedTitle(attribute.id)}`)
                .join(', ');
        },

        // unavailable, soldOutAll, soldOut, low (5 or fewer left) or inStock
        get stockState() {
            const variant = this.variant;
            if (!variant) {
                return 'unavailable';
            }
            if (!this.variants.some(other => other.hasStock)) {
                return 'soldOutAll';
            }
            if (!variant.hasStock) {
                return 'soldOut';
            }
            return variant.stock !== null && variant.stock <= 5 ? 'low' : 'inStock';
        },

        // The stock message of the current state, filled in: "Size M is ready to ship."
        get stockMessage() {
            const message = config.messages?.[this.stockState] ?? { title: '', text: '' };
            const text = message.text
                .replace('{selection}', this.selectionLabel)
                .replace('{count}', this.variant?.stock ?? '');
            return { title: message.title, text: text.charAt(0).toUpperCase() + text.slice(1) };
        },

        showVariantImage(variant) {
            if (variant.imageId) {
                this.activeImageId = variant.imageId;
            }
        },
    };
};
